import "src/assets/css/odq-preferences.scss";
import React, {useEffect, useMemo, useState} from "react";
import {KatButton, KatSpinner} from "@amzn/katal-react";
import ExemptionModal from "src/app/component/odqAndReliability/ExemptionModal";
import SLPTicketModal from "src/app/component/odqAndReliability/SLPTicketModal";
import {OdqAndReliabilityApi} from "src/app/api/OdqAndReliabilityApi";
import CustomDropdown from "src/app/component/common/CustomDropdown";
import reviewEventBus from "src/app/eventbus/ReviewEventBus";
import {ReviewEvent} from "src/app/constant/Enums";
import {isDev} from "src/app/constant/Constants";
import {MOCK_ODQ_DEFECTS, MOCK_RELIABILITY_DEFECTS} from "src/app/mock/MockReviewData";
import {buildSccLink} from "src/app/config/SccLinkConfig";
import {
    formatDateInTimezone,
    formatInTimezone,
    formatTimeOnlyInTimezone,
    formatUtcTooltip,
    isApproximateTimezone,
    localDateKey,
    timezoneAbbreviation,
    timezoneForCountry,
} from "src/app/util/TimezoneUtil";
import {GetReviewResponseData} from "src/app/api/model/response/GetReviewResponse";

interface DefectsPanelProps {
    caseId: string;
    caseSubType: string;
    transporterId: string;
    country: string;
    pdSnapshot?: GetReviewResponseData['pdSnapshot'];
}

interface Defect {
    opportunityId: string;
    trackingId?: string;
    defectType: string;
    defectDate: string;
    blockStartTime?: string;
    blockEndTime?: string;
    orderStartTime?: string;
    orderEndTime?: string;
    concessionTime?: string;
    stationCode?: string;
}

/** One defect row staged for exemption. */
interface ExemptTarget {
    opportunityId: string;
    defectType: string;
    trackingId?: string;
}

/**
 * A defect row's identity. One opportunity commonly carries several defect types, so opportunityId
 * alone is not unique -- keying selection on it made ticking one row tick its duplicate sibling, and
 * collapsed both into a single exemption request.
 */
const defectKey = (opportunityId: string, defectType: string) => `${opportunityId}::${defectType}`;

/** Only APPLIED means the defect was actually exempted; see ExemptionOutcome in the service model. */
const isApplied = (outcome?: string) => outcome === 'APPLIED';

/**
 * DefectsPanel - Tabular defect display with inline column filters, per-defect exemption, and sorting.
 * ODQ: Tracking ID | Defect Type | Order Start Time | Order End Time | Action
 * RELIABILITY: Block ID | Defect Type | Block Time | Action
 *
 * <p>Selection and exemption are keyed on (opportunityId, defectType), not opportunityId, because one
 * opportunity routinely carries several defect types and LMAQ auditors need to exempt them under
 * different reason codes.
 */
const DefectsPanel: React.FC<DefectsPanelProps> = ({caseId, caseSubType, transporterId, country, pdSnapshot}) => {
    const [defects, setDefects] = useState<Defect[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [filterType, setFilterType] = useState<string>("");
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [selectAll, setSelectAll] = useState(false);
    const [showExemptModal, setShowExemptModal] = useState(false);
    const [exemptTargets, setExemptTargets] = useState<ExemptTarget[]>([]);
    const [exemptError, setExemptError] = useState<string>("");
    const [showSLPModal, setShowSLPModal] = useState(false);
    const [filterDate, setFilterDate] = useState<string>("");
    const [filterStation, setFilterStation] = useState<string>("");
    const [filterOpsStart, setFilterOpsStart] = useState<string>("");

    const isODQ = caseSubType === "ODQ";

    const [showPreferences, setShowPreferences] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() =>
        new Set(isODQ
            ? ['trackingId', 'defectType', 'defectDate', 'station', 'opsStart', 'opsEnd', 'concessionTime']
            : ['blockTime', 'defectType'])
    );
    const [tempPageSize, setTempPageSize] = useState(pageSize);
    const [tempColumns, setTempColumns] = useState(new Set(visibleColumns));

    const ALL_COLUMNS = isODQ
        ? [{key: 'trackingId', label: 'Tracking ID'}, {key: 'opportunityId', label: 'Opportunity ID'}, {key: 'defectType', label: 'Defect Type'},
           {key: 'defectDate', label: 'Defect Date'}, {key: 'station', label: 'Station'},
           {key: 'opsStart', label: 'Ops Delivery Window Start'}, {key: 'opsEnd', label: 'Ops Delivery Window End'},
           {key: 'concessionTime', label: 'Concession Time'}]
        : [{key: 'blockTime', label: 'Block Time'}, {key: 'opportunityId', label: 'Opportunity ID'}, {key: 'defectType', label: 'Defect Type'}];

    const openPreferences = () => { setTempPageSize(pageSize); setTempColumns(new Set(visibleColumns)); setShowPreferences(true); };
    const savePreferences = () => { setPageSize(tempPageSize); setVisibleColumns(new Set(tempColumns)); setCurrentPage(1); setShowPreferences(false); };
    const col = (key: string) => visibleColumns.has(key);

    useEffect(() => {
        const handler = () => setShowSLPModal(true);
        reviewEventBus.on(ReviewEvent.SHOW_SLP_TICKET_MODAL, handler);
        return () => { reviewEventBus.off(ReviewEvent.SHOW_SLP_TICKET_MODAL, handler); };
    }, []);

    useEffect(() => { loadDefects(); }, []);

    useEffect(() => {
        const visibleKeys = new Set(filteredDefects.map(d => defectKey(d.opportunityId, d.defectType)));
        setSelectedKeys(prev => new Set(Array.from(prev).filter(key => visibleKeys.has(key))));
        setSelectAll(false);
    }, [filterType, filterDate, filterStation, filterOpsStart]);

    const loadDefects = async () => {
        if (isDev()) {
            setDefects(isODQ ? MOCK_ODQ_DEFECTS : MOCK_RELIABILITY_DEFECTS);
        } else if (pdSnapshot?.defects) {
            setDefects(pdSnapshot.defects.map(d => ({
                opportunityId: d.opportunityId,
                ...(isODQ && {trackingId: d.enrichedFields?.tracking_id || "N/A"}),
                defectType: d.defectType,
                defectDate: d.defectDate,
                orderStartTime: isODQ
                    ? (d.enrichedFields?.delivery_window_start_date_time_in_iso || "")
                    : "",
                orderEndTime: isODQ
                    ? (d.enrichedFields?.delivery_window_end_date_time_in_iso || "")
                    : "",
                blockStartTime: !isODQ
                    ? (d.enrichedFields?.shift_start_date_time_in_iso || "")
                    : "",
                blockEndTime: !isODQ
                    ? (d.enrichedFields?.shift_end_date_time_in_iso || "")
                    : "",
                stationCode: d.enrichedFields?.station_code || "N/A",
                concessionTime: d.enrichedFields?.dnr_concession_date_time_in_iso || "",
            })));
        } else {
            setDefects([]);
        }
        setLoading(false);
    };

    const toTarget = (d: Defect): ExemptTarget => ({
        opportunityId: d.opportunityId,
        defectType: d.defectType,
        trackingId: d.trackingId,
    });

    const handleExempt = (defect: Defect) => {
        setExemptError("");
        setExemptTargets([toTarget(defect)]);
        setShowExemptModal(true);
    };

    /**
     * Opportunity ids selected more than once, i.e. the same opportunity picked under two different
     * defect types.
     *
     * <p>A bulk submission carries ONE category and reason for the whole batch, and MERIT keys an
     * exemption on (transporter, category, opportunityType, opportunity) — so such a pair would resolve
     * to a single exemption record and the second defect would come back unexempted. Rather than guess
     * which one the auditor meant, the bulk action is blocked and they are asked to exempt those rows
     * one at a time, where each gets its own category and reason.
     */
    const duplicateOpportunityIds = (rows: Defect[]) => {
        const seen = new Set<string>();
        const duplicates = new Set<string>();
        rows.forEach(d => {
            if (seen.has(d.opportunityId)) {
                duplicates.add(d.opportunityId);
            }
            seen.add(d.opportunityId);
        });
        return Array.from(duplicates);
    };

    const handleBulkExempt = () => {
        const source = selectAll
            ? filteredDefects
            : filteredDefects.filter(d => selectedKeys.has(defectKey(d.opportunityId, d.defectType)));

        const duplicates = duplicateOpportunityIds(source);
        if (duplicates.length > 0) {
            setExemptError(
                `Cannot bulk exempt: ${duplicates.length} opportunity ID(s) are selected under more than `
                + `one defect type (${duplicates.slice(0, 3).join(', ')}`
                + `${duplicates.length > 3 ? `, +${duplicates.length - 3} more` : ''}). `
                + `A bulk exemption applies a single category and reason code to every selected defect, `
                + `which cannot cover two defect types on the same opportunity. Exempt those defects `
                + `individually using the Exempt button on each row.`);
            return;
        }

        setExemptError("");
        setExemptTargets(source.map(toTarget));
        setShowExemptModal(true);
    };

    /** How a defect row is labelled in the case comment. */
    const describe = (opportunityId: string, defectType?: string) => {
        // The service echoes defectType on every result, but it is optional on the contract so that the
        // service can ship ahead of this website. During that window a result may carry no defectType,
        // and matching on both fields would find nothing -- losing the tracking ID from the case
        // comment. Narrow by defectType only when it is present, and fall back to the matched row's own
        // defect type for the label.
        const d = defects.find(def => def.opportunityId === opportunityId
            && (defectType ? def.defectType === defectType : true));
        const tid = d?.trackingId;
        const hasTrackingId = isODQ && tid && tid !== 'N/A' && tid !== '';
        const idDisplay = hasTrackingId
            ? `TrackingID: ${tid}, Opportunity ID: ${opportunityId}`
            : `Opportunity ID: ${opportunityId}`;
        return `${idDisplay}, Defect: ${defectType || d?.defectType || '(unspecified)'}`;
    };

    const handleExemptSubmit = async (category: string, reasonCode: string, ticketId: string) => {
        const opportunityType = caseSubType === "ODQ" ? "DELIVERY" : "RESERVATION";
        // One request item per selected defect row. Rows are NOT de-duplicated by opportunityId --
        // collapsing them is what silently dropped a defect. The bulk action already refused a selection
        // containing one opportunity under two defect types, so a same-opportunity pair cannot get here.
        const exemptionRequests = exemptTargets.map(t => ({
            opportunityId: t.opportunityId,
            opportunityType,
            defectType: t.defectType,
        }));
        setExemptError("");
        try {
            const response = await OdqAndReliabilityApi.submitExemptions(
                caseId, transporterId, category, reasonCode, ticketId, exemptionRequests);
            const results = response?.data?.results || [];

            // Anything other than APPLIED left a defect un-exempted, including ALREADY_EXISTS, which
            // only means MERIT refused to overwrite an existing record for that (opportunity, category).
            // Reported in the banner and the case comment; the table itself shows no per-row status.
            const notApplied = results.filter((r: any) => !isApplied(r.outcome));
            if (notApplied.length > 0) {
                const first = notApplied[0];
                setExemptError(`${notApplied.length} of ${results.length} defect(s) were not exempted. `
                    + `${first.defectType || first.opportunityId}: ${first.message || first.outcome}`);
            }

            const lines: string[] = [
                `Exemption submitted for ${exemptionRequests.length} defect(s), Category: ${category}, `
                + `Reason: ${reasonCode}, Ticket: ${ticketId}`
            ];
            results.forEach((r: any) => {
                lines.push(`${describe(r.opportunityId, r.defectType)} \u2192 ${r.outcome}`
                    + `${r.message ? `: ${r.message}` : ''}`);
            });
            await OdqAndReliabilityApi.addComment(caseId, lines.join('\n'));
        } catch (error: any) {
            setExemptError('Failed to submit exemption request. Please try again.');
            console.error('[DefectsPanel] Failed to submit exemptions', error);

            const failLines: string[] = [
                `Exemption submission failed for ${exemptionRequests.length} defect(s), `
                + `Category: ${category}, Reason: ${reasonCode}, Ticket: ${ticketId}`,
                `Error: ${error?.message || 'Unknown error'}`
            ];
            exemptionRequests.forEach(r => {
                failLines.push(`${describe(r.opportunityId, r.defectType)} \u2192 FAILED`);
            });
            await OdqAndReliabilityApi.addComment(caseId, failLines.join('\n'));
        } finally {
            setSelectedKeys(new Set());
            setSelectAll(false);
            setShowExemptModal(false);
            setExemptTargets([]);
        }
    };

    const defectTypes = useMemo(() => {
        return Array.from(new Set(defects.map(d => d.defectType))).sort();
    }, [defects]);

    const isValidFilter = (val: string | undefined) => val && val !== "N/A" && val !== "—" && val !== "";

    /*
     * Andes gives every timestamp as ISO 8601 in UTC, so everything below converts out of UTC
     * into the DP's country zone. Previously the ODQ times were shifted by a hardcoded +5:30 and
     * labelled IST -- which is what forced the auditor to convert back to the DP's local time by
     * hand -- while the Reliability block times were left in UTC and *also* labelled IST, so that
     * column was mislabelled by the full 5h30m.
     *
     * The label is now always derived from the zone actually used, so a value cannot claim a zone
     * it was not converted to.
     */
    const displayTimezone = useMemo(() => timezoneForCountry(country), [country]);

    // The DP's local defect date, not the UTC one. Shared by the column, the filter options and
    // the filter predicate so all three agree; see localDateKey for why the UTC date is wrong.
    const defectDateOf = (d: Defect) => localDateKey(d.defectDate, displayTimezone);

    const defectDates = useMemo((): {value: string; title: string}[] => {
        const dates = (Array.from(new Set(defects.map(defectDateOf).filter(isValidFilter))) as string[]).sort();
        return dates.map(t => ({value: t, title: t}));
    }, [defects, displayTimezone]);

    const stationCodes = useMemo(() => {
        return (Array.from(new Set(defects.map(d => d.stationCode).filter(isValidFilter))) as string[]).sort();
    }, [defects]);

    const opsStartTimes = useMemo(() => {
        return (Array.from(new Set(defects.map(d => d.orderStartTime).filter(isValidFilter))) as string[]).sort();
    }, [defects]);

    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
    const [sortField, setSortField] = useState<string>("defectDate");


    const filteredDefects = useMemo(() => {
        let filtered = defects.filter(d => !filterType || d.defectType === filterType);
        if (filterDate) {
            filtered = filtered.filter(d => defectDateOf(d) === filterDate);
        }
        if (filterStation) filtered = filtered.filter(d => d.stationCode === filterStation);
        if (filterOpsStart) filtered = filtered.filter(d => d.orderStartTime === filterOpsStart);
        filtered.sort((a, b) => {
            const getSortValue = (d: Defect) => {
                if (sortField === "orderStartTime") return new Date(d.orderStartTime || d.defectDate).getTime();
                if (sortField === "concessionTime") return d.concessionTime ? new Date(d.concessionTime).getTime() : 0;
                return new Date(d.defectDate).getTime();
            };
            const firstTimestamp = getSortValue(a);
            const secondTimestamp = getSortValue(b);
            return sortDirection === "asc" ? firstTimestamp - secondTimestamp : secondTimestamp - firstTimestamp;
        });
        return filtered;
        // displayTimezone belongs here because the filterDate predicate resolves each defect's
        // local date through defectDateOf, which closes over it.
    }, [defects, filterType, filterDate, filterStation, filterOpsStart, sortDirection, sortField,
        displayTimezone]);

    const totalPages = Math.ceil(filteredDefects.length / pageSize);
    const startIdx = (currentPage - 1) * pageSize;
    const pageDefects = filteredDefects.slice(startIdx, startIdx + pageSize);

    /**
     * Hover text for every converted timestamp: the UTC instant, plus a warning when the zone is
     * only a country-level default.
     *
     * US, CA, MX, BR, AU and ES span several zones, so the country's zone is right for most DPs in
     * that country and wrong for the rest -- and a converted time reads as authoritative either way.
     * The station code on each row is the signal that could disambiguate, but no station-to-zone
     * source is available to the UI, so the unambiguous UTC value stays one hover away.
     */
    const utcTitle = (iso: string | undefined) => {
        const utc = formatUtcTooltip(iso);
        if (!utc) return undefined;
        return isApproximateTimezone(country)
            ? `${utc} — shown in ${displayTimezone}, the default for ${country}, `
                + `which spans multiple timezones`
            : utc;
    };

    const formatTime = (iso: string | undefined) => formatInTimezone(iso, displayTimezone);
    const formatBlockTime = (iso: string | undefined) => formatTimeOnlyInTimezone(iso, displayTimezone);
    const formatBlockDate = (iso: string | undefined) => formatDateInTimezone(iso, displayTimezone);

    const toggleSelect = (key: string) => {
        setSelectedKeys(prev => {
            const base = selectAll
                ? new Set(filteredDefects.map(d => defectKey(d.opportunityId, d.defectType)))
                : new Set(prev);
            base.has(key) ? base.delete(key) : base.add(key);
            return base;
        });
        setSelectAll(false);
    };

    const toggleSelectAllPage = () => {
        const pageKeys = pageDefects.map(d => defectKey(d.opportunityId, d.defectType));
        const allSelected = pageKeys.every(key => selectedKeys.has(key));
        setSelectedKeys(prev => {
            const next = new Set(prev);
            pageKeys.forEach(key => allSelected ? next.delete(key) : next.add(key));
            return next;
        });
        setSelectAll(false);
    };

    const selectAllItems = () => {
        setSelectedKeys(new Set(filteredDefects.map(d => defectKey(d.opportunityId, d.defectType))));
        setSelectAll(true);
    };

    const clearSelection = () => { setSelectedKeys(new Set()); setSelectAll(false); };

    const pageSelectableKeys = pageDefects.map(d => defectKey(d.opportunityId, d.defectType));
    const allPageSelected = pageSelectableKeys.length > 0 && pageSelectableKeys.every(key => selectedKeys.has(key));
    const totalSelectable = filteredDefects.length;
    const selectedCount = selectAll ? totalSelectable : selectedKeys.size;

    if (loading) {
        return (
            <div className="odq-card">
                <div className="odq-card__title">Defects ({caseSubType})</div>
                <KatSpinner size="small" />
            </div>
        );
    }

    return (
        <div className="odq-card odq-card--flex" data-testid="defects-panel">
            <div className="odq-card__title">Defects ({caseSubType})</div>

            <div className="odq-defects__header">
                <span>Showing {startIdx + 1}-{Math.min(startIdx + pageSize, filteredDefects.length)} of {filteredDefects.length}</span>
                <button className="odq-prefs-gear" onClick={openPreferences} title="Preferences" data-testid="prefs-gear-btn">⚙</button>
            </div>

            {selectedCount > 0 && (
                <div className="odq-bulk-bar" data-testid="bulk-bar">
                    <input type="checkbox" checked={true} onChange={clearSelection}
                           className="odq-checkbox" data-testid="bulk-clear-checkbox" />
                    <span className="odq-bulk-bar__count">
                        {selectedCount} items selected{allPageSelected && !selectAll && ` (All on this page)`}
                    </span>
                    <KatButton label="Bulk Exempt" variant="primary" size="small"
                               onClick={handleBulkExempt} data-testid="bulk-exempt-btn" />
                    <KatButton label="Clear Selection" variant="secondary" size="small"
                               onClick={clearSelection} data-testid="clear-selection-btn" />
                    {allPageSelected && !selectAll && totalSelectable > pageSize && (
                        <span className="odq-bulk-bar__select-all" onClick={selectAllItems}
                              data-testid="select-all-link">
                            Select all {totalSelectable} items
                        </span>
                    )}
                    <KatButton variant="secondary" size="small" label="×" onClick={clearSelection}
                               data-testid="bulk-close-btn" />
                </div>
            )}


            {exemptError && (
                <div className="odq-exempt-error" data-testid="exempt-error-banner">
                    <span>{exemptError}</span>
                    <button onClick={() => setExemptError("")} className="odq-exempt-error__dismiss">×</button>
                </div>
            )}

            <div className="odq-defects">
                <table className="odq-defects__table">
                    <thead>
                        <tr>
                            <th className="odq-defects__th-check">
                                <input type="checkbox" checked={allPageSelected}
                                       onChange={toggleSelectAllPage} className="odq-checkbox"
                                       data-testid="select-all-page-checkbox" />
                            </th>
                            {isODQ && col('trackingId') && <th>Tracking ID</th>}
                            {col('opportunityId') && <th>Opportunity ID</th>}
                            {!isODQ && col('blockTime') && (
                                <th className="odq-defects__th-sortable" data-testid="sort-block-time"
                                    onClick={() => { setSortField("defectDate"); setSortDirection(d => d === "asc" ? "desc" : "asc"); }}>
                                    Block Time {sortDirection === "asc" ? "↑" : "↓"}
                                </th>
                            )}
                            {col('defectType') && <th>Defect Type</th>}
                            {isODQ && col('defectDate') && <th className="odq-defects__th-sortable" data-testid="sort-defect-date"
                                onClick={() => { setSortField("defectDate"); setSortDirection(d => d === "asc" ? "desc" : "asc"); }}>
                                Defect Date {sortDirection === "asc" ? "↑" : "↓"}
                            </th>}
                            {isODQ && (col('station') || col('opsStart') || col('opsEnd')) && (
                                <>
                                    {col('station') && <th>Station</th>}
                                    {col('opsStart') && <th className="odq-defects__th-sortable" data-testid="sort-order-start"
                                        onClick={() => { setSortField("orderStartTime"); setSortDirection(d => d === "asc" ? "desc" : "asc"); }}>
                                        Ops Delivery Window Start {sortDirection === "asc" ? "↑" : "↓"}
                                    </th>}
                                    {col('opsEnd') && <th>Ops Delivery Window End</th>}
                                </>
                            )}
                            {isODQ && col('concessionTime') && (
                                <th className="odq-defects__th-sortable" data-testid="sort-concession-time"
                                    onClick={() => { setSortField("concessionTime"); setSortDirection(d => d === "asc" ? "desc" : "asc"); }}>
                                    Concession Time {sortDirection === "asc" ? "↑" : "↓"}
                                </th>
                            )}
                            <th>Action</th>
                        </tr>
                        <tr className="odq-defects__filter-row">
                            <td></td>
                            {isODQ && col('trackingId') && <td></td>}
                            {col('opportunityId') && <td></td>}
                            {!isODQ && col('blockTime') && <td>
                                {filterDate ? (
                                    <span className="odq-tag"><span className="odq-tag__label">{filterDate}</span>
                                        <button className="odq-tag__dismiss" onClick={() => { setFilterDate(""); setCurrentPage(1); }}>×</button></span>
                                ) : (
                                    <CustomDropdown label="" placeholder="Filters..." options={defectDates}
                                        value={filterDate} onChange={(val) => { setFilterDate(val); setCurrentPage(1); }} testId="filter-block-time" />
                                )}
                            </td>}
                            {col('defectType') && <td>
                                {filterType ? (
                                    <span className="odq-tag" data-testid="active-filter-tag">
                                        <span className="odq-tag__label">{filterType}</span>
                                        <button className="odq-tag__dismiss" data-testid="dismiss-filter-btn"
                                                onClick={() => { setFilterType(""); setCurrentPage(1); }}>×</button>
                                    </span>
                                ) : (
                                    <CustomDropdown label="" placeholder="Filters..."
                                        options={defectTypes.map(t => ({value: t, title: t}))}
                                        value={filterType}
                                        onChange={(val) => { setFilterType(val); setCurrentPage(1); }}
                                        testId="filter-type" />
                                )}
                            </td>}
                            {isODQ && col('defectDate') && <td>
                                {filterDate ? (
                                    <span className="odq-tag"><span className="odq-tag__label">{filterDate}</span>
                                        <button className="odq-tag__dismiss" onClick={() => { setFilterDate(""); setCurrentPage(1); }}>×</button></span>
                                ) : (
                                    <CustomDropdown label="" placeholder="Filters..." options={defectDates}
                                        value={filterDate} onChange={(val) => { setFilterDate(val); setCurrentPage(1); }} testId="filter-date" />
                                )}
                            </td>}
                            {isODQ && col('station') && <td>
                                {filterStation ? (
                                    <span className="odq-tag"><span className="odq-tag__label">{filterStation}</span>
                                        <button className="odq-tag__dismiss" onClick={() => { setFilterStation(""); setCurrentPage(1); }}>×</button></span>
                                ) : (
                                    <CustomDropdown label="" placeholder="Filters..." options={stationCodes.map(t => ({value: t, title: t})) as {value: string; title: string}[]}
                                        value={filterStation} onChange={(val) => { setFilterStation(val); setCurrentPage(1); }} testId="filter-station" />
                                )}
                            </td>}
                            {isODQ && col('opsStart') && <td>
                                {filterOpsStart ? (
                                    <span className="odq-tag"><span className="odq-tag__label">{formatTime(filterOpsStart)}</span>
                                        <button className="odq-tag__dismiss" onClick={() => { setFilterOpsStart(""); setCurrentPage(1); }}>×</button></span>
                                ) : (
                                    <CustomDropdown label="" placeholder="Filters..." options={opsStartTimes.map(t => ({value: t, title: formatTime(t)})) as {value: string; title: string}[]}
                                        value={filterOpsStart} onChange={(val) => { setFilterOpsStart(val); setCurrentPage(1); }} testId="filter-ops-start" />
                                )}
                            </td>}
                            {isODQ && col('opsEnd') && <td></td>}
                            {isODQ && col('concessionTime') && <td></td>}
                            <td></td>
                        </tr>

                    </thead>
                    <tbody>
                        {pageDefects.map(defect => {
                            const key = defectKey(defect.opportunityId, defect.defectType);
                            return (
                            <tr key={key}
                                className={`${selectedKeys.has(key) ? "odq-defects__row--selected" : ""}`}>
                                <td className="odq-defects__td-check">
                                    <input type="checkbox" checked={selectedKeys.has(key)}
                                           onChange={() => toggleSelect(key)}
                                           className="odq-checkbox"
                                           data-testid={`select-row-${key}`} />
                                </td>
                                {isODQ && col('trackingId') && (
                                    <td>
                                        {defect.trackingId && defect.trackingId !== "N/A" ? (
                                            <a href={buildSccLink(defect.trackingId, country)} target="_blank" rel="noreferrer">
                                                {defect.trackingId}
                                            </a>
                                        ) : 'N/A'}
                                    </td>
                                )}
                                {col('opportunityId') && <td className="odq-defects__cell--id">{defect.opportunityId}</td>}
                                {!isODQ && col('blockTime') && (
                                    <td title={utcTitle(defect.blockStartTime)}>
                                        {defect.blockStartTime ? (
                                            <>
                                                {/* One zone label for the range, taken from the start
                                                    instant so it reflects DST on that date. */}
                                                <div className="odq-defects__block-time" data-testid="defect-block-time">
                                                    {formatBlockTime(defect.blockStartTime)} - {formatBlockTime(defect.blockEndTime)}
                                                    {' '}{timezoneAbbreviation(defect.blockStartTime, displayTimezone)}
                                                </div>
                                                <div className="odq-defects__block-date">{formatBlockDate(defect.blockStartTime)}</div>
                                            </>
                                        ) : '—'}
                                    </td>
                                )}
                                {col('defectType') && <td>{defect.defectType}</td>}
                                {isODQ && col('defectDate') && <td title={utcTitle(defect.defectDate)}
                                                                   data-testid="defect-date">
                                    {defectDateOf(defect)}
                                </td>}
                                {isODQ && (col('station') || col('opsStart') || col('opsEnd')) && (
                                    <>
                                        {col('station') && <td>{defect.stationCode || '—'}</td>}
                                        {col('opsStart') && <td title={utcTitle(defect.orderStartTime)}
                                                               data-testid="defect-ops-start">
                                            {defect.orderStartTime ? (
                                                <>{formatTime(defect.orderStartTime)}</>
                                            ) : '—'}
                                        </td>}
                                        {col('opsEnd') && <td title={utcTitle(defect.orderEndTime)}>
                                            {defect.orderEndTime ? (
                                                <>{formatTime(defect.orderEndTime)}</>
                                            ) : '—'}
                                        </td>}
                                    </>
                                )}
                                {isODQ && col('concessionTime') && <td title={utcTitle(defect.concessionTime)}>
                                    {defect.concessionTime ? formatTime(defect.concessionTime) : ''}
                                </td>}
                                <td>
                                    {/* Action only -- no per-row status, ever. Submission outcomes are
                                        reported in the error banner above the table and in the case
                                        comment. The button therefore stays available for a retry
                                        regardless of what the last attempt returned. */}
                                    <KatButton label="Exempt"
                                                 onClick={() => handleExempt(defect)}
                                                 data-testid={`exempt-btn-${key}`} />
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="odq-pagination">
                <KatButton label="<" className="odq-pagination__btn"
                           disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}
                           data-testid="prev-page-btn" />
                <span className="odq-pagination__info">Page {currentPage} of {totalPages}</span>
                <KatButton label=">" className="odq-pagination__btn"
                           disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}
                           data-testid="next-page-btn" />
            </div>

            <ExemptionModal
                visible={showExemptModal}
                transporterId={transporterId}
                targetIds={exemptTargets.map(t => t.opportunityId)}
                selectedTrackingIds={exemptTargets.map(t =>
                    (t.trackingId && t.trackingId !== 'N/A' && t.trackingId !== '—')
                        ? t.trackingId : t.opportunityId)}
                country={country}
                opportunityType={caseSubType === "ODQ" ? "DELIVERY" : "RESERVATION"}
                onSubmit={handleExemptSubmit}
                onClose={() => { setShowExemptModal(false); setExemptTargets([]); }}
            />


            {showPreferences && (
                <div className="odq-prefs-overlay" data-testid="prefs-modal">
                    <div className="odq-prefs-modal">
                        <div className="odq-prefs-modal__header">
                            <h3>Preferences</h3>
                            <button className="odq-prefs-modal__close" onClick={() => setShowPreferences(false)}>×</button>
                        </div>
                        <div className="odq-prefs-modal__body">
                            <div className="odq-prefs-modal__section">
                                <h4>Page size</h4>
                                {[10, 25, 50, 75, 100].map(size => (
                                    <label key={size} className="odq-prefs-radio">
                                        <input type="radio" name="pageSize" checked={tempPageSize === size}
                                               onChange={() => setTempPageSize(size)} />
                                        {size} items
                                    </label>
                                ))}
                            </div>
                            <div className="odq-prefs-modal__section">
                                <div className="odq-prefs-modal__toggle-header">
                                    <button className={`odq-prefs-btn--link ${tempColumns.size < ALL_COLUMNS.length ? 'odq-prefs-btn--outline' : ''}`}
                                            onClick={() => setTempColumns(new Set(ALL_COLUMNS.map(c => c.key)))}>Select all</button>
                                    <button className={`odq-prefs-btn--link ${tempColumns.size > 0 ? 'odq-prefs-btn--outline' : ''}`}
                                            onClick={() => {
                                                const locked = ALL_COLUMNS.filter(c =>
                                                    ((c.key === 'defectType' && !!filterType) || (c.key === 'station' && !!filterStation) ||
                                                     (c.key === 'opsStart' && !!filterOpsStart) || (c.key === 'defectDate' && !!filterDate) ||
                                                     (c.key === 'blockTime' && !!filterDate)) && tempColumns.has(c.key)
                                                ).map(c => c.key);
                                                setTempColumns(new Set(locked));
                                            }}>Deselect all</button>
                                </div>
                                {ALL_COLUMNS.map(c => {
                                    const hasActiveFilter =
                                        (c.key === 'defectType' && !!filterType) ||
                                        (c.key === 'station' && !!filterStation) ||
                                        (c.key === 'opsStart' && !!filterOpsStart) ||
                                        (c.key === 'defectDate' && !!filterDate) ||
                                        (c.key === 'blockTime' && !!filterDate);
                                    const locked = hasActiveFilter && tempColumns.has(c.key);
                                    const toggle = () => { if (locked) return; const next = new Set(tempColumns); next.has(c.key) ? next.delete(c.key) : next.add(c.key); setTempColumns(next); };
                                    return (
                                    <div key={c.key} className={`odq-prefs-toggle-row${locked ? ' odq-prefs-toggle-row--locked' : ''}`} onClick={toggle}>
                                        <span className="odq-prefs-toggle-label">
                                            {c.label}
                                            {locked && <span className="odq-prefs-toggle-hint"> (clear filter first)</span>}
                                        </span>
                                        <span className={`odq-prefs-toggle-track${tempColumns.has(c.key) ? ' odq-prefs-toggle-track--active' : ''}${locked ? ' odq-prefs-toggle-track--locked' : ''}`}>
                                            <span className={`odq-prefs-toggle-thumb${tempColumns.has(c.key) ? ' odq-prefs-toggle-thumb--active' : ''}`} />
                                        </span>
                                    </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="odq-prefs-modal__footer">
                            <button className="odq-prefs-btn--save" onClick={savePreferences} disabled={tempColumns.size === 0} data-testid="prefs-save-btn">Save changes</button>
                        </div>
                    </div>
                </div>
            )}

            {isODQ && (
                <SLPTicketModal
                    visible={showSLPModal}
                    defects={filteredDefects}
                    onSubmit={async (cat, typ, itm, trackingIds, stationCode, defectDate) => {
                        const response = await OdqAndReliabilityApi.createSlpTicket(caseId, {
                            stationCode, transporterId, defectDate, trackingIds,
                            cti: {category: cat, type: typ, item: itm}
                        });
                        return response.data;
                    }}
                    onClose={() => setShowSLPModal(false)}
                />
            )}
        </div>
    );
};

export default DefectsPanel;
